import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Dialog, Grid, Typography } from '@mui/material';
import { faCheck, faExclamation, faTimes, IconDefinition } from '@fortawesome/pro-regular-svg-icons';

type AlertType = 'default' | 'success' | 'warning' | 'error';

export const useAlert = () => {
    const [open, setOpen] = useState<boolean>(false);
    const [type, setType] = useState<AlertType>('default');
    const [icon, setIcon] = useState<IconDefinition>();
    const [color, setColor] = useState<string>('#FFFFFF');
    const [title, setTitle] = useState<string>('');
    const [message, setMessage] = useState<React.ReactNode>(<></>);
    const [onConfirm, setOnConfirm] = useState<() => void>(() => () => {});
    const onConfirmClick = () => {
        onConfirm();
        close();
    };
    const show = (option: { type: AlertType; title: string; message: React.ReactNode; onConfirm?: () => void }) => {
        setType(option.type);
        setTitle(option.title);
        setMessage(option.message);
        setOnConfirm(option.onConfirm ? () => option.onConfirm : () => () => {});
        setOpen(true);
    };
    const close = () => setOpen(false);
    useEffect(() => {
        const delayResponse = setTimeout(() => {
            if (!open) {
                setTitle('');
                setMessage(<></>);
            }
        }, 1000);
        return () => clearTimeout(delayResponse);
    }, [open]);
    useEffect(() => {
        switch (type) {
            case 'default':
                setIcon(undefined);
                setColor('#FFFFFF');
                break;
            case 'error':
                setIcon(faTimes);
                setColor('#E15241');
                break;
            case 'success':
                setIcon(faCheck);
                setColor('#67AD5B');
                break;
            case 'warning':
                setIcon(faExclamation);
                setColor('#F19D38');
                break;
            default:
                break;
        }
    }, [type]);
    return {
        open,
        setOpen,
        type,
        setType,
        icon,
        setIcon,
        color,
        setColor,
        title,
        setTitle,
        message,
        setMessage,
        onConfirm,
        setOnConfirm,
        onConfirmClick,
        show,
        close,
    };
};

export interface AlertProps extends ReturnType<typeof useAlert> {}

export const Alert = (props: AlertProps) => {
    return (
        <Dialog
            fullWidth={true}
            maxWidth={'xs'}
            open={props.open}
            onClose={props.close}
            onKeyDown={(e) => {
                if (e.key == 'Enter') {
                    // 阻止事件的冒泡和默認行為
                    e.stopPropagation();
                    e.preventDefault();
                    props.onConfirmClick();
                }
                if (e.key == 'Escape') props.close();
            }}
        >
            <Grid container item>
                <Grid container item justifyContent={'flex-start'} gap={1} margin={1} fontSize={32} fontWeight={'bold'}>
                    <div style={{ color: 'white', borderRadius: 4, marginRight: 8, backgroundColor: props.color }}>
                        {true ? <FontAwesomeIcon fixedWidth icon={faCheck} /> : <></>}
                    </div>
                    {props.title}
                </Grid>
                <Grid container item justifyContent={'flex-start '} gap={1} margin={1}>
                    <Typography color='textSecondary' style={{ whiteSpace: 'pre-wrap' }}>
                        {props.message}
                    </Typography>
                </Grid>
                <Grid container item justifyContent={'flex-end'} gap={1} margin={1}>
                    <Button variant='outlined' color='error' disableElevation onClick={props.close}>
                        取消
                    </Button>
                    <Button variant='contained' color='primary' disableElevation onClick={props.onConfirmClick}>
                        確定
                    </Button>
                </Grid>
            </Grid>
        </Dialog>
    );
};
